import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { UserType } from '../constants';

import logo from '../images/sla-logo.png';
import './ResetPassword.scss';
import { authFetch } from '../services/authFetch';
import { validateMaxConsecutiveCharacters } from '../utilities/validation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

function ResetPassword({ fromEmail = false }) {
    const navigate = useNavigate();
    const { state } = useLocation();
    const params = useParams();

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm({ mode: 'all', criteriaMode: 'all' });

    const [isLoading, setIsLoading] = useState(false);
    const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
    const [showVerifyPasswordErrors, setShowVerifyPasswordErrors] = useState(false);

    const shouldUseState = state !== null;
    const newPassword = watch('password', '');
    const currentPassword = watch('currentPassword', '');
    const username = shouldUseState ? state.username : params.username;

    if (!username) {
        return <Navigate to="/login" />;
    }

    function handleResetPassword() {
        const formData = new FormData();
        formData.append('username', username);
        formData.append('newPassword', newPassword);

        const isSlipUser = shouldUseState ? state.slip : params.userType === 'B';

        if (isSlipUser) {
            formData.append('userType', UserType.SLIPUser);
        } else {
            formData.append('userType', UserType.SLAUser);
        }

        if (fromEmail) {
            formData.append('token', params.token);
        } else {
            formData.append('currentPassword', currentPassword);
        }

        setIsLoading(true);
        authFetch(
            `${window.AnalyticsPortalConfig.apiAddress}/PortalSessions/reset-password`,
            {
                method: 'POST',
                body: formData,
            }
        )
            .then(response => {
                if (response.ok) {
                    toast.success('Successfully updated password!');
                    navigate('/login');
                } else {
                    if (response.status === 403) {
                        toast.error('Incorrect password. Please try again.');
                    } else if (response.status === 401) {
                        return response.text();
                    }
                }
            })
            .then(errorText => {
                toast.error(errorText);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => setIsLoading(false));
    }

    function handleCancel(event) {
        navigate('/login');
    }

    return (
        <div className="container-fluid h-100">
            <div className="row h-100">
                <div className="col-12 col-lg-6 order-2 order-lg-1 align-self-center">
                    <div id="login-form-wrapper" className="mx-auto">
                        <form onSubmit={handleSubmit(handleResetPassword)}>
                            <h4 id="login-header" className="mb-3">
                                {fromEmail
                                    ? 'Password reset requested'
                                    : 'Password change is required'}
                            </h4>
                            {!fromEmail && (
                                <div className="form-group mb-4">
                                    <label htmlFor="currentPassword">
                                        Current Password
                                    </label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        id="currentPassword"
                                        placeholder="Your current password"
                                        defaultValue={state.currentPassword}
                                        autoComplete="current-password"
                                        {...register('currentPassword', {
                                            required: true,
                                        })}
                                    />
                                    {errors.currentPassword &&
                                        errors.currentPassword.type === 'required' && (
                                            <div className="text-danger m-1">
                                                <small>Required</small>
                                            </div>
                                        )}
                                </div>
                            )}
                            <div className="form-group mb-4 position-relative">
                                <label htmlFor="password">New Password</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder="Your new password"
                                    defaultValue=""
                                    onFocus={() => setShowPasswordRequirements(true)}
                                    autoComplete="new-password"
                                    {...register('password', {
                                        onBlur: () => setShowPasswordRequirements(false),    
                                        required: true,
                                        minLength: 8,
                                        validate: {
                                            max2ConsecutiveCharacters: value =>
                                                // no more than 2 consecutive letters or numbers
                                                validateMaxConsecutiveCharacters(value, 2),
                                            noUsername: value =>
                                                !new RegExp(`${username}`, 'i').test(
                                                    value
                                                ), // does not include username
                                            includesDiverseCharacters: value => {
                                                const conditions = [
                                                    /[a-z]/.test(value), // includes a lowercase letter
                                                    /[A-Z]/.test(value), // includes an uppercase letter
                                                    /\d/.test(value), // includes a number
                                                    /[~!@#$%^&*]/.test(value), // includes a special character
                                                ];
                                                return (
                                                    conditions.filter(
                                                        cond => cond === true
                                                    ).length >= 3
                                                );
                                            },
                                        },
                                        deps: ['verifyPassword'],
                                    })}
                                />
                                {
                                    showPasswordRequirements && (
                                        <div className="mt-2 xs-font requirements-box">
                                            <div className="header">Password Requirements</div>
                                            <div>
                                                {errors?.password?.types?.minLength || newPassword.length < 3 ? 
                                                    <FontAwesomeIcon icon={faExclamationCircle} color='red' /> : 
                                                    <FontAwesomeIcon icon={faCheckCircle} color='green' />}
                                                Password must be a minimum of 8 characters
                                            </div>
                                            <div>
                                                {errors?.password?.types?.max2ConsecutiveCharacters || newPassword.length < 3 ? 
                                                    <FontAwesomeIcon icon={faExclamationCircle} color='red' /> : 
                                                    <FontAwesomeIcon icon={faCheckCircle} color='green' />}
                                                Password may not have more than 2 consecutive letters or numbers
                                            </div>
                                            <div>
                                                {errors?.password?.types?.noUsername || newPassword.length < 3 ? 
                                                    <FontAwesomeIcon icon={faExclamationCircle} color='red' /> : 
                                                    <FontAwesomeIcon icon={faCheckCircle} color='green' />}
                                                Password may not include your username
                                            </div>
                                            <div>
                                                {errors?.password?.types?.includesDiverseCharacters || newPassword.length < 3 ? 
                                                    <FontAwesomeIcon icon={faExclamationCircle} color='red' /> : 
                                                    <FontAwesomeIcon icon={faCheckCircle} color='green' />}
                                                Password must include at least 3 of the following:
                                                <ul style={{margin: 0}}>
                                                    <li>Lowercase letter </li>
                                                    <li>Uppercase letter </li>
                                                    <li>Number</li>
                                                    <li>Special characters: ~!@#$%^&*</li>
                                                </ul>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                            <div className="form-group mb-4 position-relative">
                                <label htmlFor="verifyPassword">
                                    Verify New Password
                                </label>
                                <input
                                    type="password"
                                    className="form-control"
                                    id="verifyPassword"
                                    placeholder="Please confirm password"
                                    onFocus={() => setShowVerifyPasswordErrors(true)}
                                    autoComplete="new-password"
                                    {...register('verifyPassword', {
                                        required: true,
                                        onBlur: () => setShowVerifyPasswordErrors(false),
                                        validate: {
                                            mustMatch: value => value === newPassword,
                                        },
                                    })}
                                />
                                {
                                    showVerifyPasswordErrors && (
                                        <div className="mt-2 xs-font requirements-box">
                                        <div className="header">Verify Password</div>
                                            <div>
                                                {errors?.verifyPassword?.type === "required" ? 
                                                    <FontAwesomeIcon icon={faExclamationCircle} color='red' /> : 
                                                    <FontAwesomeIcon icon={faCheckCircle} color='green' />}
                                                Password is required
                                            </div>
                                            <div>
                                                {errors?.verifyPassword?.type === "mustMatch" ? 
                                                    <FontAwesomeIcon icon={faExclamationCircle} color='red' /> : 
                                                    <FontAwesomeIcon icon={faCheckCircle} color='green' />}
                                                Passwords must match
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                            <div className="d-flex justify-content-between">
                                <button
                                    id="change-password-button"
                                    type="submit"
                                    className="btn btn-primary btn-block mt-4 mr-1"
                                    disabled={isLoading}
                                >
                                    Change Password
                                </button>
                                <button
                                    id="cancel-button"
                                    type="button"
                                    className="btn btn-secondary btn-block mt-4 ml-1"
                                    onClick={handleCancel}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                <div
                    id="login-graphic-container"
                    className="col-12 col-lg-6 order-1 order-lg-2"
                >
                    <img src={logo} alt="SLA Analytics Logo" />
                </div>
            </div>
        </div>
    );
}


export default ResetPassword;
